import { useCallback } from "react";

import { useDesktop } from "@/hooks/use-desktop";
import { useSegmentTrack } from "@/hooks/use-segment";
import { appMainService } from "@/ipc";
import { useEditorURL, type PlainEditor } from "@/queries/editor-queries";
import { useStartEnvironment } from "@/queries/environment-queries";
import { showStart } from "@/routes/environments/phase";
import { isValidURL } from "@/utils/url-validation";
import { type Environment } from "gitpod-next-api/gitpod/v1/environment_pb";

export function useOpenEditor() {
    const editorURL = useEditorURL();
    const startEnvironment = useStartEnvironment();
    const { isDesktop } = useDesktop();
    const segmentTrack = useSegmentTrack();

    const track = useCallback(
        (editor: PlainEditor) => {
            segmentTrack("Open Editor", {
                editor_id: editor.id,
                editor_alias: editor.alias,
                editor_name: editor.name,
            });
        },
        [segmentTrack],
    );

    return useCallback(
        async (editor: PlainEditor, environment?: Environment, version?: string) => {
            try {
                // Start the environment if it's stopped — fire-and-forget since the
                // editor will connect once it's up and we don't want to delay opening.
                if (environment && showStart(environment)) {
                    startEnvironment.mutate({ environmentId: environment.id });
                }

                let newWindow: WindowProxy | null = null;
                // opening "about:blank" does not work in desktop. There, we use shell.openExternal instead.
                if (isValidURL(editor.urlTemplate) && !isDesktop) {
                    const url = new URL(editor.urlTemplate);
                    if (url.protocol === "https:") {
                        // We open the window with about:blank first and only later set the real URL once we have it.
                        // This is because if we try to open the window after fetching our editor URL, we risk being
                        // too late and the browser blocking the popup.
                        newWindow = window.open("about:blank", "_blank");
                    }
                }

                const result = await editorURL.mutateAsync({ editorId: editor.id, environment, version });
                const url = result.url;
                if (!url) {
                    return;
                }

                if (isDesktop && appMainService) {
                    await appMainService.openExternal({ url });
                    return;
                }

                if (!newWindow) {
                    // Create hidden iframe as using window.location.href
                    // kills vscode websocket connection
                    const iframe = document.createElement("iframe");
                    iframe.src = url;
                    iframe.style.display = "none";
                    document.body.appendChild(iframe);
                    setTimeout(() => document.body.removeChild(iframe), 2000);
                } else {
                    newWindow.location.href = url;
                }
                track(editor);
            } catch (e) {
                console.error(e);
            }
        },
        [isDesktop, editorURL, startEnvironment, track],
    );
}
